import React from "react";

import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Col, Container, Image, Row } from "react-bootstrap";

import SectionHeader from "@/components/SectionHeader.component";

import i18n from "../../i18n";

import type { IPress } from "@/interfaces/IPress";

const PressComponent = (): React.JSX.Element => {
  const { t, language } = i18n;

  const items: IPress[] = [
    {
      date: new Date(2026, 2, 3),
      logo: "/images/press/canadianbusiness.svg",
      publisher: "Canadian Business",
      color: "#FFFFFF",
      link: "https://canadianbusiness.com/ideas/how-green-building-can-tackle-the-housing-crisis/",
      title: t("How Green Building Can Tackle the Housing Crisis"),
      description: t(
        "Industrial-scale sustainable construction offers Canada a way to ease housing shortages while protecting the planet, says Parinaz Pakniat",
      ),
    },

    {
      date: new Date(2024, 6, 12),
      logo: "/images/press/betakit.png",
      publisher: "Betakit",
      color: "#000000",
      link: "https://betakit.com/xatoms-juno-win-big-at-startupfest-2024/#:~:text=pakville",
      title: t("PakVille™ steals the show with grandmothers' choice award"),
      description: t(
        "Montréal-based PakVille™ won the hearts of this year's grandmothers. The startup designs and develops modular spaces in easy-to-assemble units made from recycled plastic bottles.",
      ),
    },

    {
      date: new Date(2024, 1, 10),
      logo: "/images/press/la-presse.svg",
      publisher: "La Presse",
      color: "#ed1c23",
      link: "https://www.lapresse.ca/maison/2024-02-10/pakville/une-piece-de-plus-au-jardin.php",
      title: t("An additional room in the garden"),
      description: t(
        "Sometimes, all that's missing in a house to not feel cramped is an additional room. The young Montreal-based company PakVille™ offers a practical and relatively economical solution to add a pavilion in the yard that can serve as an office, a yoga room, or even a mini-house.",
      ),
    },

    {
      date: new Date(2024, 0, 16),
      logo: "/images/press/noovomoi.svg",
      publisher: "Noovo Moi",
      color: "#fcfbf6",
      link: "https://www.noovomoi.ca/style-et-maison/maison/mini-maisons-jardin.html",
      title: t("Mini Yard Houses: A New Trend Taking 2024 by Storm"),
      description: t(
        "This local company offers luxurious small dwellings that come with everything you need: doors, windows, electrical wiring, recessed lighting fixtures, and outlets!",
      ),
    },

    {
      date: new Date(2024, 0, 9),
      logo: "/images/press/quoifaireenfamille.svg",
      publisher: "Quoi faire en famille",
      color: "#f5f5f5",
      link: "https://quoifaireenfamille.com/nouvelles/communiques-de-presse/les-grandes-tendances-de-la-maison-seront-a-lexpohabitation-de-montreal-des-le-8-fevrier/",
      title: t("Savings and Green Expertise on the Agenda!"),
      description: t(
        "PakVille™ merges elegance with eco-responsible living. From the stylish poker room to the inspiring art studio, and the tranquil meditation sanctuary, PakVille™ effortlessly turns dreams into reality!",
      ),
    },

    {
      date: new Date(2024, 0, 9),
      logo: "/images/press/sortirauquebec.svg",
      publisher: "Sortir au Québec",
      color: "#f5f5f5",
      link: "https://sortirauquebec.com/nouvelles/communiques-de-presse/les-grandes-tendances-de-la-maison-seront-a-lexpohabitation-de-montreal-des-le-8-fevrier/",
      title: t("Savings and Green Expertise on the Agenda!"),
      description: t(
        "PakVert™ panels, made from recycled plastic bottles, are redefining the concept of home extensions.",
      ),
    },

    {
      date: new Date(2024, 0, 8),
      logo: "/images/press/city-news.jpg",
      publisher: "City News",
      color: "#fff",
      link: "https://www.youtube.com/watch?v=gBncKfCwHtE",
      title: t("Montreal HomeExpo at the Olympic Stadium"),
      description: t(
        '"This is a golden opportunity to meet tradespeople face-to-face" said Jean Saad of the Montreal HomeExpo, as 200 experts in renovations will be at the Olympic Stadium until Feb. 11, with a focus on recycling, green innovations, and real estate.',
      ),
    },

    {
      date: new Date(2024, 0, 8),
      logo: "/images/press/be-one-radio.png",
      publisher: "BE ONE | RADIO",
      color: "#000",
      link: "https://www.be1radio.com/montreal-homeexpo-se-prolongara-hasta-el-domingo",
      title: t("Montreal HomeExpo to run through Sunday"),
      description: t(
        "The Montréal HomeExpo will be held at the Olympic Stadium starting today and running through Sunday, for those interested in plans for current or future renovations to their homes and more.",
      ),
    },
  ];

  items.sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <Container>
      <SectionHeader
        eyebrow={t("In The Press")}
        title={t("Stories about PakVille")}
        description={t(
          "A curated selection of coverage about PakVille, PakVert™ technology, sustainable housing and the future of construction.",
        )}
      />
      <Row>
        {items.map((item: IPress, index: number) => (
          <Col key={index} className="mb-4" sm={6} xl={4} xs={12}>
            <Card className="d-flex align-items-center justify-content-center h-100 shadow flex-column rounded p-1">
              <Card.Header
                className="w-100 d-flex align-items-center"
                style={{
                  gap: "2rem",
                  background: "#FFF",
                  marginTop: "0.35rem",
                  borderColor: "#EEEBE9",
                }}
              >
                <div
                  className="rounded d-flex align-items-center justify-content-center"
                  style={{
                    minWidth: "100px",
                    minHeight: "100px",
                    maxWidth: "100px",
                    maxHeight: "100px",
                    backgroundColor: item.color,
                  }}
                >
                  <Image
                    rounded
                    alt={item.title}
                    className="mw-100"
                    src={item.logo}
                  />
                </div>

                <div className="modelSubHeader fw-bold fs-3">
                  {item.publisher}
                </div>
              </Card.Header>

              <Card.Body
                className="h-100 p-3 d-flex align-items-start flex-column rounded-end"
                style={{ backgroundColor: "#FFFFFF" }}
              >
                <Card.Title
                  className="fs-4 mt-3 mb-3"
                  style={{ color: "var(--primary-color)" }}
                >
                  {item.title}
                </Card.Title>

                <Card.Text className="mb-4">{item.description}</Card.Text>
              </Card.Body>

              <Card.Footer
                className="d-flex align-items-center justify-content-between w-100 flex-wrap"
                style={{
                  fontSize: "13px",
                  background: "#FFF",
                  border: "none",
                }}
              >
                <a href={item.link} rel="noreferrer" target="_blank">
                  <Button
                    className="btn-outline border-0"
                    size="sm"
                    style={{ marginLeft: "-0.5rem" }}
                    variant="ps-4 pe-2 "
                  >
                    {t("read_more")}
                    <FontAwesomeIcon className="ms-2" icon={faExternalLink} />
                  </Button>
                </a>

                <div className="ms-md-auto">
                  {item.date.toLocaleDateString(language, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default PressComponent;
